import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import {
  MachineTypeForm,
  type MachineTypeFormValues,
} from '@/components/machine-types/MachineTypeForm';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { toast } from '@/lib/toast';
import { createMachineTypeRequest } from '@/services/machine-types';
import type { CreateMachineTypeBody, MachineTypeDetail } from '@/types/machine-types';

export function MachineTypeCreatePage(): JSX.Element {
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const createMutation = useMutation<MachineTypeDetail, ApiError, { body: CreateMachineTypeBody }>({
    mutationFn: ({ body }) => createMachineTypeRequest(body),
    onSuccess: (created) => {
      toast.success(`Machine type '${created.name}' created`);
      void queryClient.invalidateQueries({ queryKey: ['machine-types'] });
      void navigate('/machine-types');
    },
    onError: (err) => {
      setErrorMessage(err.detail ?? err.message);
      toast.error('Could not create machine type', { description: err.detail ?? err.message });
    },
  });

  if (!permissions.includes('machineTypes.create')) {
    return <Navigate to="/machine-types" replace />;
  }

  function handleSubmit(values: MachineTypeFormValues): void {
    setErrorMessage(null);
    const body: CreateMachineTypeBody = {
      name: values.name,
      description: values.description ?? null,
      customFields: values.customFields,
      isActive: values.isActive,
    };
    createMutation.mutate({ body });
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Create Machine Type</h1>
        <p className="text-muted-foreground">
          Define a new machine type and its custom-field schema.
        </p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>New machine type</CardTitle>
          <CardDescription>
            Name, description, and the custom-field schema rows that machines of this type fill in.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <MachineTypeForm
            mode="create"
            onSubmit={handleSubmit}
            submitting={createMutation.isPending}
            errorMessage={errorMessage}
          />
        </CardContent>
      </Card>
    </div>
  );
}
